import React from 'react';
import { Key, Send, Loader2 } from 'lucide-react';
import PageIndexSidebar from '../../PageIndexSidebar';
import { PDFCanvasStage } from './PDFCanvasStage';

export interface DrawModeLayoutProps {
  file: { url: string; id?: string } | null;
  numPages: number | null;
  pageNumber: number;
  pageIndex: any;
  annotationCountsByPage?: Record<number, number>;
  pdfDocument: any;
  containerRef: React.RefObject<HTMLDivElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  isPageFullyLoaded: boolean;
  scale: number;
  panX: number;
  panY: number;
  isPanning: boolean;
  pdfRenderScale: number;
  devicePixelRatio: number;
  pageRotations: Record<number, number>;
  showSnapshotActions: boolean;
  snapshotSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  autoExtractKeyOnSnapshot: boolean;
  showDrawModeEscapeHint: boolean;
  canvasCursor: string;
  isUpgradingRes: boolean;
  handDrawnProps: any;
  onNavigateToPage: (page: number) => void;
  onDownloadPDF: () => void;
  onHomeClick?: () => void;
  onDocumentLoadSuccess: (pdf: any) => void;
  onPageLoadSuccess: (page: any) => void;
  onPhase1Complete: () => void;
  onMouseDown: React.MouseEventHandler<HTMLDivElement>;
  onMouseMove: React.MouseEventHandler<HTMLDivElement>;
  onMouseUp: React.MouseEventHandler<HTMLDivElement>;
  onViewportMouseLeave: () => void;
  onContextMenu: React.MouseEventHandler<HTMLDivElement>;
  onCanvasDrop: (e: React.DragEvent<HTMLCanvasElement>) => void;
  canvasToVisualCoords: (canvasX: number, canvasY: number) => { x: number; y: number };
  handleExtractKey: () => void;
  handleSendToAgent: () => void;
  handleCancelSnapshot: () => void;
}

export const DrawModeLayout: React.FC<DrawModeLayoutProps> = ({
  file,
  numPages,
  pageNumber,
  pageIndex,
  annotationCountsByPage,
  pdfDocument,
  containerRef,
  canvasRef,
  isPageFullyLoaded,
  scale,
  panX,
  panY,
  isPanning,
  pdfRenderScale,
  devicePixelRatio,
  pageRotations,
  showSnapshotActions,
  snapshotSelection,
  autoExtractKeyOnSnapshot,
  showDrawModeEscapeHint,
  canvasCursor,
  isUpgradingRes,
  handDrawnProps,
  onNavigateToPage,
  onDownloadPDF,
  onHomeClick,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  onPhase1Complete,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onViewportMouseLeave,
  onContextMenu,
  onCanvasDrop,
  canvasToVisualCoords,
  handleExtractKey,
  handleSendToAgent,
  handleCancelSnapshot,
}) => {
  if (!file) return null;

  const snapshotActions = showSnapshotActions && snapshotSelection && (() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const visualCoords = canvasToVisualCoords(
      Math.min(snapshotSelection.startX, snapshotSelection.endX),
      Math.min(snapshotSelection.startY, snapshotSelection.endY)
    );

    const left = visualCoords.x;
    const top = visualCoords.y - 50;

    return (
      <div
        className="snapshot-actions"
        style={{
          position: 'absolute',
          left,
          top,
          zIndex: 1000,
          display: 'flex',
          gap: '8px',
          background: 'var(--bg-primary)',
          padding: '8px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          border: '1px solid var(--border-color)',
          transform: `scale(${1 / scale})`,
          transformOrigin: 'top left'
        }}
      >
        {!autoExtractKeyOnSnapshot && (
          <button
            className="snapshot-action-btn primary"
            onClick={handleExtractKey}
            style={{
              padding: '6px 12px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <Key size={14} />
            Extract Power Legend
          </button>
        )}
        <button
          className="snapshot-action-btn primary"
          onClick={handleSendToAgent}
          style={{
            padding: '6px 12px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <Send size={14} />
          Send to Agent
        </button>
        <button
          className="snapshot-action-btn"
          onClick={handleCancelSnapshot}
          style={{
            padding: '6px 12px',
            background: 'var(--bg-tertiary)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500'
          }}
        >
          Cancel
        </button>
      </div>
    );
  })();

  return (
    <div className="pdf-draw-layout">
      <PageIndexSidebar
        numPages={numPages}
        pageNumber={pageNumber}
        pageIndex={pageIndex}
        annotationCountsByPage={annotationCountsByPage}
        pdfDocument={pdfDocument}
        onNavigateToPage={onNavigateToPage}
        onDownloadPDF={onDownloadPDF}
        onHomeClick={onHomeClick}
      />
      <div className="pdf-annotator-wrapper" ref={containerRef}>
        {!isPageFullyLoaded && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'var(--bg-primary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            zIndex: 50
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <Loader2 size={32} color="#3b82f6" style={{
                animation: 'spin 1s linear infinite'
              }} />
              <p style={{
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
                margin: 0
              }}>Loading drawing...</p>
            </div>
          </div>
        )}

        <PDFCanvasStage
          fileUrl={file.url}
          canvasRef={canvasRef}
          pageNumber={pageNumber}
          panX={panX}
          panY={panY}
          scale={scale}
          isPanning={isPanning}
          pdfRenderScale={pdfRenderScale}
          devicePixelRatio={devicePixelRatio}
          pageRotations={pageRotations}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          onPageLoadSuccess={onPageLoadSuccess}
          onPhase1Complete={onPhase1Complete}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onViewportMouseLeave={onViewportMouseLeave}
          onContextMenu={onContextMenu}
          onCanvasDrop={onCanvasDrop}
          canvasCursor={canvasCursor}
          isUpgradingRes={isUpgradingRes}
          handDrawnProps={handDrawnProps}
        >
          {showDrawModeEscapeHint && (
            <div className="draw-mode-escape-hint">Press Escape to exit add mode</div>
          )}
          {snapshotActions}
        </PDFCanvasStage>
      </div>
    </div>
  );
};
